import { useState, useRef, useCallback, useEffect } from 'react'

/**
 * Custom hook for managing chat with AI agent
 * Handles WebSocket connection, message history, and streaming responses
 */
export function useAgentChat(propertyKey, analysisResult, agentLogs) {
  const [messages, setMessages] = useState([])
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState(null)
  const [conversationId, setConversationId] = useState(null)
  
  const wsRef = useRef(null)
  const streamingMessageRef = useRef('')
  const connectAttemptedRef = useRef(false)
  const closedDueToErrorRef = useRef(false)
  
  // Initialize with welcome message when analysis is complete
  useEffect(() => {
    if (analysisResult && messages.length === 0) {
      const welcomeMessage = `Hello! I've analyzed this property and I'm ready to answer your questions. 

Here are some things you can ask me:
• Why did the agents give this score?
• Explain the Monte Carlo simulation results
• What are the main risks I should be aware of?
• How does this compare to other strategies?
• What would be a good offer price?
• Explain the valuation methodology

What would you like to know?`
      
      setMessages([
        {
          role: 'assistant',
          content: welcomeMessage,
          timestamp: new Date().toISOString()
        }
      ])
    }
  }, [analysisResult, messages.length])
  
  // Connect to chat WebSocket (no tight reconnect loop)
  const connect = useCallback(() => {
    if (!propertyKey || wsRef.current?.readyState === WebSocket.OPEN) {
      return
    }
    if (closedDueToErrorRef.current) {
      return
    }

    setIsConnecting(true)
    setError(null)
    connectAttemptedRef.current = true

    // Build WebSocket URL - prefer explicit env var, fallback to same host
    let wsBase = import.meta.env.VITE_WS_URL
    if (!wsBase) {
      // On Render or similar, use wss:// with same host
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      wsBase = `${protocol}//${window.location.host}`
    }
    const wsUrl = `${wsBase}/api/v1/ws/chat/${propertyKey}`
    
    console.log('[Chat] Connecting to:', wsUrl)

    console.log('Connecting to chat WebSocket:', wsUrl)
    const ws = new WebSocket(wsUrl)
    wsRef.current = ws
    let receivedReady = false

    ws.onopen = () => {
      console.log('Chat WebSocket connected')
      setIsConnected(true)
      setIsConnecting(false)

      const initMessage = {
        type: 'chat_init',
        conversation_id: conversationId
      }
      ws.send(JSON.stringify(initMessage))
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.type === 'chat_ready') receivedReady = true
        if (data.type === 'error') closedDueToErrorRef.current = true
        handleMessage(data)
      } catch (e) {
        console.error('Failed to parse chat message:', e)
      }
    }

    ws.onerror = () => {
      setError('Connection error. Please try again.')
      setIsConnected(false)
      setIsConnecting(false)
      closedDueToErrorRef.current = true
    }

    ws.onclose = () => {
      wsRef.current = null
      setIsConnected(false)
      setIsConnecting(false)
      if (!receivedReady) closedDueToErrorRef.current = true
    }
  }, [propertyKey, conversationId])
  
  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    setIsConnected(false)
    connectAttemptedRef.current = false
    closedDueToErrorRef.current = false
  }, [])
  
  // Manual reconnect - resets error state and tries again
  const reconnect = useCallback(() => {
    console.log('[Chat] Manual reconnect triggered')
    closedDueToErrorRef.current = false
    connectAttemptedRef.current = false
    setError(null)
    disconnect()
    // Small delay to ensure clean state
    setTimeout(() => connect(), 100)
  }, [connect, disconnect])
  
  // Handle incoming WebSocket messages
  const handleMessage = useCallback((data) => {
    switch (data.type) {
      case 'chat_ready':
        setConversationId(data.conversation_id)
        break
        
      case 'chat_stream':
        // Accumulate streaming chunks
        streamingMessageRef.current += data.chunk
        
        // Update the last message (assistant's streaming message)
        setMessages(prev => {
          const newMessages = [...prev]
          const lastMsg = newMessages[newMessages.length - 1]
          
          if (lastMsg && lastMsg.role === 'assistant' && lastMsg.isStreaming) {
            // Update existing streaming message
            lastMsg.content = streamingMessageRef.current
          } else {
            // Create new streaming message
            newMessages.push({
              role: 'assistant',
              content: streamingMessageRef.current,
              timestamp: new Date().toISOString(),
              isStreaming: true
            })
          }
          return newMessages
        })
        setIsStreaming(true)
        break
        
      case 'chat_complete':
        // Finalize streaming message
        setMessages(prev => {
          const newMessages = [...prev]
          const lastMsg = newMessages[newMessages.length - 1]
          
          if (lastMsg && lastMsg.role === 'assistant' && lastMsg.isStreaming) {
            lastMsg.content = data.full_response
            lastMsg.isStreaming = false
          } else {
            newMessages.push({
              role: 'assistant',
              content: data.full_response,
              timestamp: new Date().toISOString(),
              isStreaming: false
            })
          }
          return newMessages
        })
        streamingMessageRef.current = ''
        setIsStreaming(false)
        break
        
      case 'chat_error':
      case 'error':
        setError(data.message || 'Connection error')
        setIsStreaming(false)
        setMessages(prev => [
          ...prev,
          {
            role: 'system',
            content: `Error: ${data.message || 'Connection error'}`,
            timestamp: new Date().toISOString(),
            isError: true
          }
        ])
        break

      default:
        break
    }
  }, [])
  
  // Send a message to the chat agent
  const sendMessage = useCallback((content) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setError('Not connected to chat service')
      return false
    }
    
    // Add user message to local state immediately
    const userMessage = {
      role: 'user',
      content: content,
      timestamp: new Date().toISOString()
    }
    setMessages(prev => [...prev, userMessage])
    
    // Send to server
    const messageData = {
      type: 'chat_message',
      content: content,
      conversation_id: conversationId,
      analysis_result: analysisResult,
      agent_logs: agentLogs
    }
    
    wsRef.current.send(JSON.stringify(messageData))
    return true
  }, [conversationId, analysisResult, agentLogs])
  
  // Clear all messages
  const clearMessages = useCallback(() => {
    setMessages([])
    streamingMessageRef.current = ''
  }, [])
  
  // Reset error state when propertyKey changes (new property = allow connect again)
  useEffect(() => {
    connectAttemptedRef.current = false
    closedDueToErrorRef.current = false
  }, [propertyKey])

  // Auto-connect once when propertyKey and analysis are ready; don't reconnect in a loop
  useEffect(() => {
    if (!propertyKey || !analysisResult || isConnected || isConnecting) return
    if (connectAttemptedRef.current || closedDueToErrorRef.current) return
    connect()
  }, [propertyKey, analysisResult, isConnected, isConnecting, connect])
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [disconnect])
  
  return {
    messages,
    isConnected,
    isConnecting,
    isStreaming,
    error,
    conversationId,
    sendMessage,
    connect,
    disconnect,
    reconnect,
    clearMessages
  }
}

export default useAgentChat
